/**
 * <calendly-popup-button> wraps any trigger element (typically an <a>) that
 * carries a `data-calendly-popup-url` attribute and opens it as a Calendly
 * popup instead of navigating, using the Calendly widget script/styles
 * loaded globally in layout/theme.liquid.
 *
 * The wrapped element keeps a real href (falling back to a plain link/new
 * tab) so it still works if this script or the Calendly widget fails to
 * load — the popup behavior is progressive enhancement on top of that.
 *
 * Usage:
 *   <calendly-popup-button>
 *     <a
 *       href="https://calendly.com/..."
 *       data-calendly-popup-url="https://calendly.com/...?primary_color=..."
 *       target="_blank"
 *       rel="noopener"
 *     >Book now</a>
 *   </calendly-popup-button>
 *
 * Close handling (postMessage from the Calendly iframe, Escape key, and a
 * MutationObserver catch-all for however the popup's own UI closes it) is
 * registered once at module scope, not per element, since any number of
 * these can appear on a page.
 */
(() => {
  if (customElements.get('calendly-popup-button')) return;

  let lockedScrollY = 0;
  let isLocked = false;

  const lockPageScroll = () => {
    lockedScrollY = window.scrollY;
    isLocked = true;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };

  const unlockPageScroll = () => {
    if (!isLocked) return;
    isLocked = false;
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    window.scrollTo(0, lockedScrollY);
  };

  const closeCalendlyPopup = () => {
    if (typeof Calendly === 'undefined') return;
    Calendly.closePopupWidget();
    unlockPageScroll();
  };

  // Calendly's own close (X) button, or clicking its dark overlay, both
  // remove .calendly-overlay from the DOM without emitting an event we can
  // listen for — so scroll-unlock via postMessage/keydown below, plus a
  // MutationObserver as a catch-all for whichever way it closes.
  window.addEventListener('message', (event) => {
    if (event.origin !== 'https://calendly.com') return;
    if (event.data.event && event.data.event.indexOf('calendly.') === 0) {
      if (event.data.event === 'calendly.event_scheduled') closeCalendlyPopup();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeCalendlyPopup();
  });

  new MutationObserver(() => {
    if (!document.querySelector('.calendly-overlay')) unlockPageScroll();
  }).observe(document.body, { childList: true });

  class CalendlyPopupButton extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const trigger = event.target.closest('[data-calendly-popup-url]');
      if (!trigger || typeof Calendly === 'undefined') return;
      event.preventDefault();
      lockPageScroll();
      Calendly.initPopupWidget({ url: trigger.getAttribute('data-calendly-popup-url') });
    }
  }

  customElements.define('calendly-popup-button', CalendlyPopupButton);
})();
